import SetupComponent from 'App/babel/SetupRootComponent';
import { lazy } from 'react';
import AssignModalLead from 'Components/AssignModal/AssignModalLead';
import BillingRoot from 'Components/Billing/BillingRoot';
import ChecklistItem from 'Components/Admin/SalesProcess/SalesCoach/Modals/ChecklistItem';
import DecisionMakers from 'Components/Admin/SalesProcess/SalesCoach/Modals/SalesCoachModal/DecisionMakers';
import DecisionMakersPro from 'Components/Admin/SalesProcess/SalesCoach/Modals/SalesCoachModal/DecisionMakersPro';
import EditEventDomain from 'Components/Admin/EditEventDomain';
import EditProduct from 'Components/Modals/EditProduct';
import SalesCoachModal from 'Components/Admin/SalesProcess/SalesCoach/Modals/SalesCoachModal';
import Solution from 'Components/Admin/SalesProcess/SalesCoach/Modals/SalesCoachModal/Solution';
import VerifyBudget from 'Components/Admin/SalesProcess/SalesCoach/Modals/SalesCoachModal/VerifyBudget';
import VerifyTimeframe from 'Components/Admin/SalesProcess/SalesCoach/Modals/SalesCoachModal/VerifyTimeframe';
import PreviewPdf from 'Components/Admin/DocumentTemplate/PreviewPdf';
import TemplateEditor from 'Components/Admin/DocumentTemplate/TemplateEditor';
import ToggleFiscalYearModal from 'Components/Admin/ToggleFiscalYearModal';
import { checkPortedResolve } from 'App/helpers/angularRouteHelpers';
import FieldTranslationResource from 'App/babel/resources/FieldTranslations';
import logError from 'Helpers/logError';

const EasyBookingSettings = lazy(() => import('Components/Admin/EasyBookingSettingsV2/Settings'));
const EasyBookingSettingsEditorV2 = lazy(() =>
	import('Components/Admin/EasyBookingSettingsV2/Editor/EasyBookingSettingsPage')
);
const JourneyStatuses = lazy(() => import('Components/Admin/JourneyStatuses/JourneyStatuses'));
const LoginStats = lazy(() => import('../../../babel/components/Admin/LoginStats'));
const PipelineGoals = lazy(() => import('Components/Admin/Goals/PipelineGoals'));
const SalesProcessList = lazy(() => import('Components/Admin/SalesProcess/List'));
const SubscriptionIndexingList = lazy(() => import('Components/Admin/SubscriptionIndexing/SubscriptionIndexingList'));
const SharedEasyBooking = lazy(() => import('Components/Admin/EasyBookingSettingsV2/SharedEasyBooking'));

angular.module('domain.admin', ['ngRoute', 'upResources', 'security.authorization']).config([
	'$stateProvider',
	'$upModalProvider',
	function ($stateProvider, $upModalProvider) {
		// BASE PATH
		$stateProvider.state('administration', {
			abstract: true,
			controller: 'AdminCtrl as AdminCtrl',
			templateUrl: require('App/upsales/domain/admin/views/main.html?file'),
			url: '/:customerId/up-admin',
			section: 'admin',
			resolve: {
				loaded: [
					'AppService',
					function (AppService) {
						return AppService.loadedPromise;
					}
				]
			}
		});

		var isAdminOrMaAdmin = function ($q, AppService) {
			var defer = $q.defer();

			// eslint-disable-next-line promise/catch-or-return
			AppService.loadedPromise.then(function () {
				if (AppService.getSelf().administrator || AppService.getSelf().userParams.mailAdmin) {
					return defer.resolve(true);
				}
				defer.reject();
			});

			return defer.promise;
		};
		isAdminOrMaAdmin.$inject = ['$q', 'AppService'];

		var isAdmin = function ($q, AppService) {
			var defer = $q.defer();

			// eslint-disable-next-line promise/catch-or-return
			AppService.loadedPromise.then(function () {
				if (AppService.getSelf().administrator) {
					return defer.resolve(true);
				}
				defer.reject();
			});

			return defer.promise;
		};
		isAdmin.$inject = ['$q', 'AppService'];

		const isProjectAdmin = function ($q, AppService) {
			const defer = $q.defer();

			// eslint-disable-next-line promise/catch-or-return
			AppService.loadedPromise.then(function () {
				if (AppService.getSelf().projectAdmin) {
					return defer.resolve(true);
				}
				defer.reject();
			});

			return defer.promise;
		};
		isProjectAdmin.$inject = ['$q', 'AppService'];

		const isAdminOrProjectAdmin = function ($q, AppService) {
			return $q
				.all({
					isAdmin: isAdmin($q, AppService).catch(() => false),
					isProjectAdmin: isProjectAdmin($q, AppService).catch(() => false)
				})
				.then(({ isAdmin, isProjectAdmin }) => {
					return isAdmin || isProjectAdmin;
				});
		};
		isAdminOrProjectAdmin.$inject = ['$q', 'AppService'];

		var isTeamLeader = function ($q, AppService) {
			var defer = $q.defer();

			// eslint-disable-next-line promise/catch-or-return
			AppService.loadedPromise.then(function () {
				if (AppService.getSelf().teamLeader) {
					return defer.resolve(true);
				}
				defer.reject();
			});

			return defer.promise;
		};

		var isAdminAndCrm = function ($q, FeatureHelper, AppService) {
			return $q.all({
				isAdmin: isAdmin($q, AppService),
				hasCrm: FeatureHelper.isAvailableProductPromise(FeatureHelper.Product.CRM)
			});
		};
		isAdminAndCrm.$inject = ['$q', 'FeatureHelper', 'AppService'];

		async function easyBookingProAccess($q, FeatureHelper, AppService) {
			return $q.all({
				isAdminOrMaAdmin: isAdminOrMaAdmin($q, AppService),
				hasCrm: FeatureHelper.isAvailableProductPromise(FeatureHelper.Product.CRM)
			});
		}

		easyBookingProAccess.$inject = ['$q', 'FeatureHelper', 'AppService'];

		var isAdminOrTeamLeaderAndCrm = function ($q, FeatureHelper, AppService) {
			return $q.all({
				isAdminOrTeamLeader: $q
					.all({
						isAdmin: isAdmin($q, AppService).catch(() => false),
						isTeamLeader: isTeamLeader($q, AppService).catch(() => false)
					})
					.then(({ isAdmin, isTeamLeader }) => {
						return isAdmin || isTeamLeader ? Promise.resolve() : Promise.reject();
					}),
				hasCrm: FeatureHelper.isAvailableProductPromise(FeatureHelper.Product.CRM)
			});
		};

		var genericSubview = require('App/upsales/domain/admin/views/sub.html?file');
		var genericSubctrl = 'AdminSubCtrl as AdminSubCtrl';

		$stateProvider
			.state('administration.dashboard', {
				url: '/'
			})
			.state('administration.imports', {
				controller: genericSubctrl,
				templateUrl: genericSubview,
				url: '/import',
				section: 'admin',
				params: { stepId: null },
				resolve: {
					isAdmin: isAdmin
				}
			})
			.state('administration.import', {
				controller: 'ImportCtrl as AdminSubCtrl',
				templateUrl: genericSubview,
				url: '/import/:id',
				section: 'admin',
				params: { stepId: null, isNew: false },
				resolve: {
					isAdmin: isAdmin
				}
			})
			.state('administration.profile', {
				url: '/profile'
			})
			.state('administration.user', {
				controller: 'EditUserCtrl as AdminSubCtrl',
				templateUrl: genericSubview,
				url: '/user/:id',
				section: 'admin',
				resolve: {
					isAdmin: isAdmin
				}
			})
			.state('administration.activityTypes', {
				url: '/activitytypes'
			})
			.state('administration.apiKeys', {
				controller: 'ListApiKeys as AdminSubCtrl',
				templateUrl: genericSubview,
				url: '/security/apiKeys',
				section: 'admin',
				resolve: {
					isAdmin: isAdmin,
					isAvailable: [
						'FeatureHelper',
						function (FeatureHelper) {
							return checkPortedResolve('administration.apiKeys', () => {
								return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.API_KEYS);
							});
						}
					]
				}
			})
			.state('administration.scripts', {
				template: '<div></div>',
				url: '/scripts',
				section: 'admin',
				resolve: {
					isAdmin: isAdmin
				}
			})
			.state('administration.script', {
				template: '<div></div>',
				url: '/script/:id',
				section: 'admin',
				resolve: {
					isAdmin: isAdmin
				}
			})
			.state('administration.integration', {
				controller: 'ConfigureIntegration as AdminSubCtrl',
				templateUrl: genericSubview,
				url: '/integrations/configure/:id/:configure?code?setOauthToNull?tab?oAuthFieldName',
				section: 'admin'
			})
			.state('administration.editIntegration', {
				controller: 'EditIntegration as AdminSubCtrl',
				templateUrl: genericSubview,
				url: '/integrations/edit/:id',
				section: 'admin'
			})
			.state('administration.mailSettings', {
				controller: 'MailAdmin as AdminSubCtrl',
				templateUrl: genericSubview,
				url: '/mailsettings',
				section: 'admin',
				resolve: {
					isAdminOrMaAdmin: isAdminOrMaAdmin,
					meta: [
						'MailAdminMeta',
						function (MailAdminMeta) {
							return MailAdminMeta();
						}
					]
				}
			})
			.state('administration.visitSettings', {
				controller: 'VisitSettings as AdminSubCtrl',
				templateUrl: genericSubview,
				section: 'admin',
				url: '/visit',
				resolve: {
					isAdminOrMaAdmin: isAdminOrMaAdmin
				}
			})
			.state('administration.automations', {
				controller: 'ListAutomations as AdminSubCtrl',
				templateUrl: genericSubview,
				section: 'admin',
				url: '/automations',
				resolve: {
					isAdmin: isAdmin,
					isAvailable: [
						'FeatureHelper',
						function (FeatureHelper) {
							return checkPortedResolve('administration.automations', () => {
								return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.AUTOMATIONS);
							});
						}
					]
				}
			})
			.state('administration.automation', {
				controller: 'EditAutomation as AdminSubCtrl',
				templateUrl: genericSubview,
				section: 'admin',
				url: '/automation/:id',
				resolve: {
					isAdmin: isAdmin,
					isAvailable: [
						'FeatureHelper',
						function (FeatureHelper) {
							return checkPortedResolve('administration.automation', () => {
								return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.AUTOMATIONS);
							});
						}
					]
				}
			})
			.state('administration.triggers', {
				controller: 'ListTriggers as AdminSubCtrl',
				templateUrl: genericSubview,
				section: 'admin',
				url: '/triggers',
				resolve: {
					isAdminOrProjectAdmin: isAdminOrProjectAdmin,
					isAvailable: [
						'FeatureHelper',
						'AppService',
						function (FeatureHelper, AppService) {
							return checkPortedResolve('administration.triggers', () => {
								return AppService.loadedPromise.then(() => {
									if (AppService.getSelf().projectAdmin) {
										return true;
									}
									return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.TRIGGERS);
								});
							});
						}
					]
				}
			})
			.state('administration.trigger', {
				controller: 'EditTrigger as AdminSubCtrl',
				templateUrl: genericSubview,
				section: 'admin',
				url: '/trigger/:id',
				resolve: {
					isAdminOrProjectAdmin: isAdminOrProjectAdmin,
					isAvailable: [
						'FeatureHelper',
						'AppService',
						function (FeatureHelper, AppService) {
							return checkPortedResolve('administration.trigger', () => {
								return AppService.loadedPromise.then(() => {
									if (AppService.getSelf().projectAdmin) {
										return true;
									}
									return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.TRIGGERS);
								});
							});
						}
					]
				}
			})
			.state('administration.ipblock', {
				controller: genericSubctrl,
				templateUrl: genericSubview,
				url: '/ip-block',
				section: 'admin',
				resolve: {
					isAdmin: isAdmin
				}
			})
			.state('administration.salesboards', {
				url: '/salesboards'
			})

			.state('administration.loginStats', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, LoginStats, $element[0], {}, { redux: true, useSuspense: true });
					}
				],
				url: '/sign-in-stats',
				section: 'admin',
				resolve: {
					isAdmin: isAdmin
				}
			})
			.state('administration.salesboard', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$stateParams',
					function ($scope, $element, $stateParams) {
						var props = Object.assign({}, $stateParams, { $scope: $scope });
						SetupComponent($scope, window.AdminEditSalesboard, $element[0], props, { redux: true });
					}
				],
				url: '/salesboard/:id',
				section: 'admin',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.state('administration.pipelinegoals', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, PipelineGoals, $element[0], {}, { redux: true, useSuspense: true });
					}
				],
				url: '/pipeline-goals',
				section: 'admin',
				resolve: {
					isAdminOrTeamLeaderAndCrm: isAdminOrTeamLeaderAndCrm,
					softDeploy: [
						'FeatureHelper',
						function (FeatureHelper) {
							return checkPortedResolve('administration.pipelinegoals', () => {
								return FeatureHelper.hasSoftDeployAccessPromise('GOALS');
							});
						}
					]
				}
			})
			.state('administration.phonecallgoals', {
				url: '/phonecall-goals'
			})
			.state('administration.bookedappointmentgoals', {
				url: '/booked-appointment-goals'
			})
			.state('administration.completedappointmentgoals', {
				url: '/completed-appointment-goals'
			})
			.state('administration.subscriptionIndexing', {
				url: '/subscriptionIndexing'
			})
			.state('administration.subscriptionIndexingList', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent(
							$scope,
							SubscriptionIndexingList,
							$element[0],
							{},
							{ redux: true, useSuspense: true }
						);
					}
				],
				url: '/subscriptionIndexingList',
				section: 'admin',
				resolve: {
					isAdminAndCrm: isAdminAndCrm,
					isAvailable: [
						'FeatureHelper',
						function (FeatureHelper) {
							const hasSubscriptions = FeatureHelper.isAvailablePromise(
								FeatureHelper.Feature.RECURRING_ORDER
							);
							const hasIndexing = FeatureHelper.hasSoftDeployAccess('SUBSCRIPTION_INDEX_INCREASE_V2');

							return hasSubscriptions && hasIndexing;
						}
					]
				}
			})
			.state('administration.optIn', {
				url: '/optin'
			});

		// MAIN
		$stateProvider
			.state('admin', {
				abstract: true,
				controller: 'Admin as Admin',
				section: 'admin',
				templateUrl: require('App/upsales/domain/admin/views/admin.html?file'),
				url: '/:customerId/admin',
				resolve: {
					authUser: [
						'$routeGuard',
						function ($routeGuard) {
							return $routeGuard.requireUser();
						}
					]
				}
			})
			// SALESBOARDS
			.state('admin.salesboards', {
				url: '/salesboards'
			})
			.state('admin.salesboard', {
				url: '/salesboard/:id'
			})
			.state('admin.settings', {
				controller: 'Settings',
				templateUrl: require('App/upsales/domain/admin/views/settings/settings.html?file'),
				url: '/settings',
				resolve: {
					authAdmin: [
						'$routeGuard',
						function ($routeGuard) {
							return $routeGuard.requireAdmin();
						}
					]
				}
			})
			.state('administration.billing', {
				template: '<div id="admin-billing-root"></div>',
				controller: [
					'$scope',
					'$element',
					'$stateParams',
					function ($scope, $element, $stateParams) {
						SetupComponent($scope, BillingRoot, $element[0], { initialTab: $stateParams.initialTab });
					}
				],
				params: {
					alias: null,
					initialTab: null
				},
				reloadOnSearch: false,
				url: '/billing/:initialTab?/?alias',
				section: 'admin',
				resolve: {
					authAdmin: [
						'$routeGuard',
						function ($routeGuard) {
							return $routeGuard.requireBillingAdmin();
						}
					]
				}
			})
			.state('administration.optOut', {
				url: '/optOut'
			})
			.state('administration.accountsettings', {
				url: '/accountsettings'
			})
			.state('administration.salesprocesses', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, SalesProcessList, $element[0], {}, { redux: true, useSuspense: true });
					}
				],
				url: '/salesprocesses',
				section: 'admin',
				resolve: {
					isAdminAndCrm: isAdminAndCrm,
					hasIt: [
						'FeatureHelper',
						function (FeatureHelper) {
							return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.SALES_PROCESS);
						}
					]
				}
			})
			.state('administration.appointmentAvailability', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent(
							$scope,
							EasyBookingSettings,
							$element[0],
							{},
							{ redux: true, useSuspense: true }
						);
					}
				],
				url: '/appointment-availability',
				section: 'admin'
			})
			.state('administration.sharedAppointmentAvailability', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, SharedEasyBooking, $element[0], {}, { redux: true, useSuspense: true });
					}
				],
				url: '/shared-appointment-availability',
				section: 'admin',
				resolve: {
					easyBookingProAccess: function ($q, FeatureHelper, AppService) {
						return checkPortedResolve('administration.sharedAppointmentAvailability', () => {
							return easyBookingProAccess($q, FeatureHelper, AppService);
						});
					}
				}
			})
			.state('administration.sharedAppointmentAvailabilitySettings', {
				url: '/shared-appointment-availability/:id'
			})

			.state('administration.appointmentEditor', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$stateParams',
					function ($scope, $element, $stateParams) {
						SetupComponent(
							$scope,
							EasyBookingSettingsEditorV2,
							$element[0],
							{ id: $stateParams.id },
							{ redux: true, useSuspense: true }
						);
					}
				],
				url: '/appointment-editor/:id',
				section: 'admin'
			})

			.state('administration.reminders', {
				url: '/reminders'
			})
			.state('administration.mailsignature', {
				url: '/mailSignature'
			})
			.state('administration.sharedviews', {
				template: '<div id="admin-sharedviews-root"></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, window.SharedViewsAdmin, $element[0], {}, { redux: true });
					}
				],
				url: '/sharedviews',
				section: 'admin',
				resolve: {
					softDeploy: [
						'FeatureHelper',
						function (FeatureHelper) {
							return checkPortedResolve('administration.sharedviews', () => {
								return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.SHARED_VIEWS);
							});
						}
					],
					authAdmin: [
						'$routeGuard',
						function ($routeGuard) {
							return checkPortedResolve('administration.sharedviews', () => {
								return $routeGuard.requireAdmin();
							});
						}
					]
				}
			})
			.state('administration.domains', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, window.AdminDomains, $element[0], {}, { redux: true });
					}
				],
				url: '/domains',
				section: 'admin',
				resolve: {
					authAdminOrMailAdmin: [
						'$routeGuard',
						function ($routeGuard) {
							return checkPortedResolve('administration.domains', () => {
								return $routeGuard.requireAdminOrMailAdmin();
							});
						}
					]
				}
			})
			.state('administration.journeyStatuses', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, JourneyStatuses, $element[0], {}, { redux: true, useSuspense: true });
					}
				],
				url: '/journeyStatuses',
				section: 'admin',
				resolve: {
					hasFeature: [
						'FeatureHelper',
						function (FeatureHelper) {
							return checkPortedResolve('administration.journeyStatuses', () => {
								return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.JOURNEY_STATUS);
							});
						}
					],
					authAdminOrMailAdmin: [
						'$routeGuard',
						function ($routeGuard) {
							return checkPortedResolve('administration.journeyStatuses', () => {
								return $routeGuard.requireAdminOrMailAdmin();
							});
						}
					]
				}
			})
			.state('administration.journeyEditor', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$stateParams',
					function ($scope, $element, $stateParams) {
						SetupComponent(
							$scope,
							window.JourneyEditor,
							$element[0],
							{ type: $stateParams.type, id: $stateParams.id },
							{ redux: true }
						);
					}
				],
				url: '/journeyEditor/:type/:id',
				section: 'admin',
				resolve: {
					// loaded: function(AppService) {
					// 	return AppService.loadedPromise;
					// },
					softDeploy: [
						'FeatureHelper',
						function (FeatureHelper) {
							return checkPortedResolve('administration.journeyEditor', () => {
								return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.JOURNEY_STATUS);
							});
						}
					],
					authAdminOrMailAdmin: [
						'$routeGuard',
						function ($routeGuard) {
							return checkPortedResolve('administration.journeyEditor', () => {
								return $routeGuard.requireAdminOrMailAdmin();
							});
						}
					]
				}
			})
			.state('administration.singleSignOn', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					function ($scope, $element) {
						SetupComponent($scope, window.SingleSignOn, $element[0], {}, { redux: true });
					}
				],
				url: '/single-sign-on',
				section: 'admin',
				resolve: {
					isAvailable: [
						'FeatureHelper',
						function (FeatureHelper) {
							return FeatureHelper.isAvailablePromise(FeatureHelper.Feature.SINGLE_SIGN_ON);
						}
					],
					authAdmin: [
						'$routeGuard',
						function ($routeGuard) {
							return $routeGuard.requireAdmin();
						}
					]
				}
			})
			.state('administration.notificationCenter', {
				url: '/notification-center'
			})
			.state('administration.defaultMailSignature', {
				url: '/default-mail-signature'
			});

		// Modals
		$upModalProvider
			.modal('SendEmailAction', {
				templateUrl: require('App/upsales/domain/admin/views/automations/sendEmailAction.html?file'),
				controller: 'SendEmailAction',
				style: 'form default'
			})
			.modal('EditRole', {
				template: '<div id="edit-role-modal-root"></div>',
				controller: function ($scope) {
					new window.EditRoleController($scope);
				},
				rememberHeight: true,
				invisibleCurtain: true,
				style: 'new-full-screen',
				resolve: {
					isAdmin: isAdmin,
					isAvailable: function () {
						return Tools.FeatureHelper.isAvailablePromise(
							Tools.FeatureHelper.Feature.ADVANCED_ROLE_SETTINGS
						);
					}
				}
			})
			.modal('SalesCoachModal', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							SalesCoachModal,
							$element[0],
							{ id: $modalParams.id, ...$modalParams },
							{ redux: true, modal: true }
						);
					}
				],
				rememberHeight: true,
				invisibleCurtain: true,
				style: 'new-full-screen no-padding',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('ChecklistItem', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							ChecklistItem,
							$element[0],
							{ id: $modalParams.id, checklistItem: $modalParams.checklistItem || null },
							{ redux: true, modal: true }
						);
					}
				],
				rememberHeight: true,
				invisibleCurtain: true,
				style: 'new-full-screen no-padding',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('VerifyBudget', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							VerifyBudget,
							$element[0],
							{ id: $modalParams.id },
							{ redux: true, modal: true }
						);
					}
				],
				rememberHeight: true,
				style: 'new-full-screen no-padding',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('DecisionMakers', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							DecisionMakers,
							$element[0],
							{ id: $modalParams.id },
							{ redux: true, modal: true }
						);
					}
				],
				rememberHeight: true,
				invisibleCurtain: true,
				style: 'new-full-screen no-padding',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('DecisionMakersPro', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							DecisionMakersPro,
							$element[0],
							{ id: $modalParams.id },
							{ redux: true, modal: true }
						);
					}
				],
				rememberHeight: true,
				invisibleCurtain: true,
				style: 'new-full-screen no-padding',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('Solution', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							Solution,
							$element[0],
							{ id: $modalParams.id },
							{ redux: true, modal: true }
						);
					}
				],
				rememberHeight: true,
				invisibleCurtain: true,
				style: 'new-full-screen no-padding',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('VerifyTimeframe', {
				template: '<div></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							VerifyTimeframe,
							$element[0],
							{ id: $modalParams.id },
							{ redux: true, modal: true }
						);
					}
				],
				rememberHeight: true,
				invisibleCurtain: true,
				style: 'new-full-screen no-padding',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('UpdateClientAction', {
				templateUrl: require('App/upsales/domain/account/views/updateClientAction.html?file'),
				controller: 'UpdateClientAction',
				style: 'form-md default'
			})
			.modal('UpdateClientContactsAction', {
				templateUrl: require('App/upsales/domain/contact/views/updateContactAction.html?file'),
				controller: 'UpdateContactAction',
				style: 'form-md default'
			})
			.modal('AssignToUserAction', {
				templateProvider: function () {
					return Tools.$q.when('<div></div>');
				},
				controller: function ($scope, $element, $modalParams) {
					SetupComponent(
						$scope,
						AssignModalLead,
						$element[0],
						{
							...$modalParams,
							title: Tools.$translate('assign.assignLead')
						},
						{ redux: true, modal: true }
					);
				},
				initialHeight: 300,
				style: function () {
					return 'processed-by-modal dynamic';
				}
			})
			.modal('AssignToUserActionLegacy', {
				templateUrl: require('App/upsales/domain/admin/views/automations/createLeadAction.html?file'),
				controller: 'CreateLeadAction',
				style: 'confirm default'
			})
			.modal('UpdateContactAction', {
				templateUrl: require('App/upsales/domain/contact/views/updateContactAction.html?file'),
				controller: 'UpdateContactAction',
				style: 'form-md default'
			})
			.modal('SendWebhookAction', {
				templateUrl: require('App/upsales/domain/admin/views/automations/sendWebhookAction.html?file'),
				controller: 'SendWebhookAction',
				style: 'form default'
			})
			.modal('CreateActivityAction', {
				templateUrl: require('App/upsales/domain/admin/views/automations/createActivityAction.html?file'),
				style: 'form-md default',
				controller: 'CreateActivityAction'
			})
			.modal('AnonymizeContactAction', {
				template: '<div></div>',
				controller: function (
					$scope,
					$element,
					$modalParams,
					AppService,
					ContactAttributes,
					ActivityAttributes,
					AppointmentAttributes,
					OrderAttributes,
					OpportunityAttributes,
					MailAttributes,
					EventAttributes,
					AgreementAttributes
				) {
					window.AnonymizeContactController(
						$scope,
						$element,
						$modalParams,
						AppService,
						ContactAttributes,
						ActivityAttributes,
						AppointmentAttributes,
						OrderAttributes,
						OpportunityAttributes,
						MailAttributes,
						EventAttributes,
						AgreementAttributes
					);
				}
			})
			.modal('AddContactToSegmentAction', {
				template: '<div id="addToSegment__root"></div>',
				controller: function (
					$scope,
					$modalParams,
					AppService,
					FeatureHelper,
					$q,
					Segment,
					$state,
					ActionProperties,
					NotificationService,
					FilterHelper,
					MultiActions,
					$multiSelect,
					$translate
				) {
					window.AddContactToSegmentController(
						$scope,
						$modalParams,
						AppService,
						$q,
						Segment,
						$state,
						ActionProperties,
						NotificationService,
						FilterHelper,
						MultiActions,
						$multiSelect,
						$translate
					);
				}
			})
			.modal('editCustomFields', {
				template: '<div id="edit-custom-field_modal"></div>',
				controller: function ($scope, AppService, $modalParams, CustomField, SniCodes) {
					let firstElement;
					var props = {
						type: $modalParams.type,
						title: $modalParams.title,
						field: $modalParams.field,
						customFields: $modalParams.customFields,
						roles: AppService.getRoles(),
						close: function () {
							$scope.reject();
						},
						confirm: async function (field, type, tooltipTranslations, translationsToDelete, tooltipTagId) {
							var customerId = Tools.AppService.getCustomerId();
							if (tooltipTranslations) {
								const emptyValues = tooltipTranslations
									.filter(tooltip => tooltip?.id && tooltip.value === '')
									.map(tooltip => tooltip.id);
								tooltipTranslations = tooltipTranslations.filter(
									tooltip => tooltip.language && tooltip.value
								);
								translationsToDelete.push(...emptyValues);

								try {
									const uniqueTooltips = tooltipTranslations.filter(
										(tooltip, index, self) =>
											index === self.findIndex(t => t.language === tooltip.language)
									);

									const [firstTooltip, ...restTooltips] = uniqueTooltips;
									let saveRestPromises = [];
									if (firstTooltip) {
										const firstSaveResult = await FieldTranslationResource.save(firstTooltip, {
											skipNotification: true
										});
										const sharedTagId = firstSaveResult.data.tagId;

										saveRestPromises = restTooltips.map(tooltip => {
											tooltip.tagId = sharedTagId;
											return FieldTranslationResource.save(tooltip, {
												skipNotification: true
											});
										});
									}
									const deletePromises = (translationsToDelete || []).map(async deleting => {
										await FieldTranslationResource.delete(deleting, {
											skipNotification: true
										});
										if (uniqueTooltips.length === 0) {
											await FieldTranslationResource.deleteTag(tooltipTagId, {
												skipNotification: true
											});
										}
									});

									await Promise.all([...saveRestPromises, ...deletePromises]);
								} catch (err) {
									logError(err, 'Error saving tooltip translations');
								}
							}

							try {
								const res = await CustomField.customer(customerId).setType(type).save(field);
								const customFields = $modalParams.customFields;

								if (field.id) {
									const index = customFields.findIndex(f => f.id === field.id);
									customFields[index] = res.data;
								} else {
									customFields.push(res.data);
								}

								AppService.setCustomFields(type, customFields);

								$scope.resolve(res.data);
							} catch (err) {
								$scope.reject(err);
							}
						},
						SniCodes: SniCodes,
						reloadModalPosition: $scope.reloadModalPosition
					};

					$scope.$on('modal.ready', function () {
						firstElement = document.getElementById('edit-custom-field_modal');
						if (!firstElement) {
							return;
						}

						var hasNewFields = Tools.FeatureHelper.hasSoftDeployAccess('NEW_FIELDS');
						var modal = ReactTemplates.admin.newFieldsModal;

						if (hasNewFields) {
							modal = window.EditCustomField;
						}
						ReactDOM.render(React.createElement(modal, props), firstElement);
					});

					$scope.$on('$destroy', function () {
						ReactDOM.unmountComponentAtNode(firstElement);
						firstElement = undefined;
					});
				},
				style: 'form default'
			})
			.modal('AddProductModal', {
				template: '<div></div>',
				style: 'form default',
				controller: ($scope, $modalParams) => {
					SetupComponent(
						$scope,
						EditProduct,
						null,
						{ editProduct: _.cloneDeep($modalParams.product) },
						{
							modal: true,
							redux: true
						}
					);
				},
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('AddProductCategoryModal', {
				template: '<div id="addProductCategoryModal_root"></div>',
				controller: 'AddProductCategoryModal as AddProductCategoryModal',
				style: 'form-sm default modal400 scroll-y',
				resolve: {
					isAdminAndCrm: isAdminAndCrm
				}
			})
			.modal('editDocumentTemplate', {
				templateUrl: require('App/upsales/domain/admin/views/editDocumentTemplate.html?file'),
				controller: 'EditDocumentTempalteCtrl as EditDocumentTempalteCtrl',
				style: 'form-md default',
				disableBindings: true
			})
			.modal('CreateAppointmentAction', {
				templateUrl: require('App/upsales/domain/admin/views/automations/createAppointmentAction.html?file'),
				controller: 'CreateAppointmentAction',
				style: 'form-md default'
			})
			.modal('optIn', {
				template: '<div id="opt-in-modal"></div>',
				resolve: {
					template: function (StandardMailTemplate) {
						return StandardMailTemplate.find({
							name: 'systemMailTemplate.doubleOptIn',
							getSystemTemplates: true
						}).then(function (res) {
							return res.data[0];
						});
					},
					profile: function () {
						return Tools.AccountProfile.get().then(function (res) {
							return res.data;
						});
					}
				},
				controller: function ($scope) {
					$scope.$on('modal.ready', function () {
						new window.OptInModal($scope);
					});
				},
				style: 'default form'
			})
			.modal('editOptOut', {
				template: '<div id="opt-out-modal"></div>',
				resolve: {
					optOut: function ($modalParams) {
						if (!$modalParams.id) {
							return Tools.$q.when(Tools.GroupMailCategory.new());
						}
						return Tools.GroupMailCategory.get($modalParams.id).then(function (res) {
							return res.data;
						});
					}
				},
				controller: function ($scope, $modalParams) {
					$scope.$on('modal.ready', function () {
						new window.OptOutModalCtrl($scope, $modalParams.optOut);
					});
				},
				style: 'form-sm'
			})
			.modal('editStandardFields', {
				template: '<div id="edit-standard-field-modal"></div>',
				controller: function ($scope) {
					$scope.$on('modal.ready', function () {
						new window.EditStandardFieldsCtrl($scope);
					});
				},
				style: 'form modal600'
			})
			.modal('translateFields', {
				template: '<div></div>',
				controller: function ($scope, $modalParams) {
					SetupComponent($scope, window.TranslateFieldModal, null, $modalParams, {
						modal: true,
						redux: true
					});
				},
				style: 'form-wide default'
			})
			.modal('editEventDomain', {
				template: '<div></div>',
				controller: function ($scope, $modalParams) {
					SetupComponent($scope, EditEventDomain, null, $modalParams, {
						modal: true,
						redux: true
					});
				},
				style: 'form-sm default'
			})
			.modal('sharedViewsModal', {
				template: '<div></div>',
				controller: function ($scope, $modalParams) {
					SetupComponent($scope, window.SharedViewsModal, null, $modalParams, {
						modal: true,
						redux: true
					});
				},
				style: 'default'
			})
			.modal('PreviewTemplate', {
				template: '<div id="addProductModal_root"></div>',
				controller: [
					'$scope',
					'$element',
					'$modalParams',
					function ($scope, $element, $modalParams) {
						SetupComponent(
							$scope,
							PreviewPdf,
							$element[0],
							{
								pdfUrl: $modalParams.pdfUrl,
								upModal: $modalParams.upModal,
								pdfTemplateResource: $modalParams.pdfTemplateResource
							},
							{ redux: true, modal: true }
						);
					}
				],
				style: 'default pdf-preview PreviewPdfModal'
			})
			.modal('templateEditor', {
				template: '<div></div>',
				controller: function ($scope, $element, $modalParams) {
					SetupComponent($scope, TemplateEditor, $element[0], $modalParams, {
						modal: true,
						redux: true
					});
				},
				invisibleCurtain: true,
				style: 'form-wide default new-full-screen no-padding'
			})
			.modal('toggleFiscalYear', {
				template: '<div></div>',
				controller: function ($scope, $element, $modalParams) {
					SetupComponent($scope, ToggleFiscalYearModal, $element[0], $modalParams, {
						modal: true
					});
				},
				style: 'form-sm default',
				resolve: {
					isAdmin: isAdmin
				}
			});
	}
]);
